import React, { useState, useEffect } from 'react';
import { Button } from "./button";
import { Loader2, RefreshCw, Eye, EyeOff, KeyRound } from "lucide-react";
import { useToast } from "./use-toast";
import { Checkbox } from './checkbox';
import { Label } from './label';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { trackActivityEvents } from '../utils/ga4Events';
import { formatSyncDate, formatTimeAgo } from '../utils/dateFormat';

import GarminLogo from './img/garmin_logo_white.png'

const garminBaseUrl = process.env.REACT_APP_API_URL ?
  (process.env.REACT_APP_API_URL.includes('localhost') ?
    `${process.env.REACT_APP_API_URL}/api/garmin` :
    `${process.env.REACT_APP_API_URL}/garmin`) :
  '/api/garmin';

const GarminComponent = ({ garminStatus, onStatusUpdate, filteredActivities, onSetFilteredActivities, fromDate, toDate, onFromDateChange, onToDateChange, allDates, onAllDatesChange }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();

  // const [filteredActivities, setFilteredActivities] = useState({});
  const [commutes, setCommutes] = useState(false);
  const [allDatesLocal, setAllDatesLocal] = useState(allDates);
  const [fromDateLocal, setFromDateLocal] = useState(fromDate);
  const [toDateLocal, setToDateLocal] = useState(toDate);

  // useEffect(() => {
  //   if (garminStatus?.connected && garminStatus?.activities) {
  //     const initialFilteredActivities = {};
  //     Object.keys(garminStatus.activities).forEach(activity => {
  //       initialFilteredActivities[activity] = true;
  //     });
  //     if (onSetFilteredActivities) {
  //       onSetFilteredActivities(initialFilteredActivities);
  //     }
  //   }
  // }, [garminStatus?.activities]);

  useEffect(() => {
    // Resume polling from the backend's truth, not localStorage — logout
    // wipes localStorage, but a sync kicked off before logout is still
    // running server-side and its progress should keep updating on re-login.
    (async () => {
      const status = await checkGarminStatus();
      if (status?.isLoading) {
        startPollingForCompletion();
      }
    })();
  }, []);

  const updateGarminData = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      setIsLoading(true);
      const response = await fetch(`${garminBaseUrl}/update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        trackActivityEvents.garminRefreshError();
        throw new Error('Failed to fetch Garmin update');
      }

      return await response.json();
    } catch (error) {
      console.error('Error checking Garmin update:', error);
    } finally {
      setIsLoading(false);
    }

  }

  const checkGarminStatus = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      setIsLoading(true);
      const response = await fetch(`${garminBaseUrl}/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch Garmin status');
      }

      const data = await response.json();
      if (onStatusUpdate) {
        onStatusUpdate({
          connected: data.connected,
          isLoading: data.isLoading,
          activities: data.activities,
          lastSync: data.lastSync,
          error: data.error,
          activityCount: data.activityCount || 0,
          currentProgress: data.currentProgress || 0,
          expectedCount: data.expectedCount || 0
        });
      }

      // Initialize activity filters if connected
      // if (data.connected && data.activities) {
      //   const initialFilteredActivities = {};
      //   Object.keys(data.activities).forEach(activity => {
      //     initialFilteredActivities[activity] = true;
      //   });
      //   if (onSetFilteredActivities) {
      //     onSetFilteredActivities(initialFilteredActivities);
      //   }
      // }

      return data;
    } catch (error) {
      console.error('Error checking Garmin status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGarminConnect = async () => {
    if (!email || !password) {
      toast({
        title: "Error",
        description: "Please enter login of your Garmin account",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    setIsLoading(true);
    const token = localStorage.getItem('token');
    trackActivityEvents.garminConnectStart();

    try {
      const response = await fetch(`${garminBaseUrl}/connect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.error || 'Failed to connect to Garmin';
        console.error('Garmin connection error:', errorMessage);
        trackActivityEvents.garminConnectError();
        throw new Error(errorMessage);
      }

      toast({
        title: "Success",
        description: "Successfully connected to Garmin",
        variant: "success",
        duration: 3000,
      });

      // Update status instead of reloading
      if (onStatusUpdate) {
        onStatusUpdate({
          connected: true,
          isLoading: data.isLoading || false,
          activities: null
        });
      }

      // Poll for data completion if loading
      if (data.isLoading) {
        startPollingForCompletion();
      }

    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "An error occurred connecting to Garmin",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
      setShowForm(false);
      setEmail('');
      setPassword('');
    }
  };

  const startPollingForCompletion = () => {
    const pollInterval = setInterval(async () => {
      const status = await checkGarminStatus();
      if (!status?.isLoading) {
        // Sync completed, clear interval and localStorage flag
        clearInterval(pollInterval);

        localStorage.removeItem('garminPollingActive');
        toast({
          title: "Complete",
          description: "Garmin activities have been synced!",
          variant: "success",
          duration: 3000,
        });
      }
    }, 12000); // Garmin syncs are slow (polyline fetch per activity); 20s is plenty.

    // Store the interval ID in localStorage as a string
    localStorage.setItem('garminPollingActive', 'active');

    // Clear interval after 30 minutes max (a fresh full sync can take a while)
    setTimeout(() => {
      clearInterval(pollInterval);
      localStorage.removeItem('garminPollingActive');
    }, 1800000);
  };

  const handleActivitySelectionChange = (selectedActivities) => {
    if (onSetFilteredActivities) {
      onSetFilteredActivities(selectedActivities);
    }
  };

  // Date filter handlers
  const handleCommuteChanged = (checked) => {
    setCommutes(checked);
  };

  const handleAllDatesChangeLocal = (checked) => {
    setAllDatesLocal(checked);
    onAllDatesChange(checked);
  };

  const handleFromDateChangeLocal = (date) => {
    setFromDateLocal(date);
    onFromDateChange(date);
  };

  const handleToDateChangeLocal = (date) => {
    setToDateLocal(date);
    onToDateChange(date);
  };

  // Refresh Garmin data
  const handleRefresh = async () => {
    toast({
      title: "Refreshing",
      description: "Fetching latest Garmin data...",
      duration: 3000,
    });

    trackActivityEvents.garminRefreshStart();
    await updateGarminData();
    const status = await checkGarminStatus();
    if (status?.isLoading) {
      startPollingForCompletion();
    }
  };

  const handleReauthorize = () => {
    setShowForm(true);
  };

  // if (isLoading && !garminStatus.isLoading) {
  //   return (
  //     <div className="flex flex-col items-center space-y-2">
  //       <div className="flex items-center justify-center w-60 h-12 bg-gray-100 rounded-md">
  //         <Loader2 className="mr-2 h-4 w-4 animate-spin" />
  //         <span className="text-gray-700">Loading Garmin data...</span>
  //       </div>
  //       <span className="text-gray-700 text-center text-red-500">
  //         Please wait!
  //       </span>
  //     </div>
  //   );
  // }

  if (showForm) {
    return (
      <div className="flex justify-center items-center">
        <div className="bg-white p-6 rounded-md shadow-md w-full max-w-md">
          <h2 className="text-2xl font-bold mb-6 text-center">Sign In to your Garmin account</h2>
          <div className="mb-6 border-b pb-4">
            <label className="block text-gray-700 font-medium mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-gray-700 font-medium">
                Password <span className="text-red-500">*</span>
              </label>
              {/* <a href="#" className="text-blue-500 hover:underline text-sm">
            Forgot Password?
          </a> */}
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center pr-3"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-500" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-500" />
                )}
              </button>
            </div>
          </div>
          {/* <div className="flex items-center mb-6">
            <input
              type="checkbox"
              id="remember-me"
              className="h-4 w-4 text-blue-500"
            />
            <label htmlFor="remember-me" className="ml-2 text-gray-700">
              Remember Me
            </label>
          </div> */}
          <Button
            onClick={handleGarminConnect}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 rounded"
          >
            Sign In
          </Button>
          {/* <div className="mt-6 text-center">
            <span className="text-gray-600">Don't want to use credentials?</span>
            <Button
              onClick={() => {
                setShowForm(false);
                setEmail('');
                setPassword('');
              }}
              className="ml-2 text-blue-500 hover:underline"
              variant="link"
            >
              Cancel
            </Button>
          </div> */}
          <p className="mt-4 text-center text-xs text-gray-500">
            By connecting your Garmin account, you're authorizing this app to access your activity data. Your credentials are transmitted securely via HTTPS. We store authentication tokens rather than your password!
          </p>
        </div>
      </div>
    )
  }

  if (garminStatus.isLoading) {
    // Calculate progress percentage
    const progressPercentage = garminStatus.expectedCount > 0
      ? Math.min(100, Math.round((garminStatus.currentProgress / garminStatus.expectedCount) * 100))
      : 0;

    return (
      <div className="flex flex-col items-center space-y-4">
        <div className="flex items-center justify-center w-80 h-12 bg-gray-100 rounded-2xl">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          <span className="text-gray-700">Syncing Garmin activities...</span>
        </div>

        {garminStatus.expectedCount > 0 && (
          <div className="w-80 flex flex-col items-center">
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600">
              {garminStatus.currentProgress} of {garminStatus.expectedCount} activities processed ({progressPercentage}%)
            </p>
          </div>
        )}

        <span className="text-gray-700 text-center">
          This may take a few minutes.
        </span>
      </div>
    );
  }

  // Compact sync header only — per-activity selection + date/commute filters
  // are handled in the ActivityFilterPanel mounted by the dashboard.
  if (garminStatus.connected && garminStatus.activities) {
    return (
      <div className="w-full flex justify-between items-center gap-2 text-sm text-gray-700">
        <div>
          <span className="font-semibold">{garminStatus.activityCount}</span> activities synced
          {garminStatus.lastSync && (
            <> on {formatSyncDate(garminStatus.lastSync)} <span className="text-xs text-gray-500">({formatTimeAgo(garminStatus.lastSync)})</span></>
          )}
        </div>
        <div className="flex items-center space-x-1">
          <Button onClick={handleRefresh} className="p-1" variant="ghost" title="Refresh Garmin data">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={handleReauthorize} className="p-1" variant="ghost" title="Reauthorize Garmin (if you changed password)">
            <KeyRound className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center space-y-4">
      <Button
        onClick={() => setShowForm(true)}
        className="w-48 h-auto rounded-2xl bg-gray-900 text-white border-0 hover:bg-gray-600 flex flex-col items-center justify-center py-2"
      >
        <span className='mb-1'>Connect to</span>
        <img src={GarminLogo} alt="Garmin Logo" className="h-8" />
      </Button>
      <span>Syncing will take up to 10 minutes!</span>
    </div>
  );
};

export default GarminComponent;